import { useState } from "react";
import type { ReactNode } from "react";
import { Loader2, PanelLeftClose, PanelLeftOpen, Plus } from "lucide-react";

export type Iso900114001MasterDetailItem = {
  id: string | number;
  label: ReactNode;
  shortLabel?: ReactNode;
  title?: string;
  count: number;
};

type Props = {
  eyebrow: string;
  title: string;
  countLabel: string;
  createLabel: string;
  items: Iso900114001MasterDetailItem[];
  selectedId: string | number | null;
  loading?: boolean;
  emptyText: string;
  onCreate: () => void;
  onSelect: (id: string | number) => void;
  children: ReactNode;
  collapsible?: boolean;
};

export function Iso900114001MasterDetailTable({
  eyebrow,
  title,
  countLabel,
  createLabel,
  items,
  selectedId,
  loading = false,
  emptyText,
  onCreate,
  onSelect,
  children,
  collapsible = true,
}: Props) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <section className={`iso9001-master-detail-panel${collapsed ? " is-collapsed" : ""}`}>
      <div className="iso9001-master-detail-toolbar">
        <div>
          <div className="calidad-toolbar-eyebrow">{eyebrow}</div>
          <h3 className="calidad-toolbar-title">{title}</h3>
        </div>
        <div className="calidad-toolbar-meta">
          <span className="calidad-toolbar-count">
            {loading ? "Cargando..." : countLabel}
          </span>
          <button type="button" className="calidad-primary-button" onClick={onCreate}>
            <Plus size={15} />
            {createLabel}
          </button>
        </div>
      </div>

      <div className="iso9001-master-detail-layout">
        <aside className="iso9001-master-detail-sidebar" aria-label={title}>
          {collapsible ? (
            <button
              type="button"
              className="iso9001-master-detail-collapse"
              title={collapsed ? "Ampliar listado" : "Reducir listado"}
              aria-label={collapsed ? "Ampliar listado" : "Reducir listado"}
              onClick={() => setCollapsed((current) => !current)}
            >
              {collapsed ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
              <span>{collapsed ? "Ampliar" : "Reducir"}</span>
            </button>
          ) : null}

          <div className="iso9001-master-detail-list">
            {loading && items.length === 0 ? (
              <div className="iso9001-master-detail-empty">
                <Loader2 size={20} className="animate-spin" />
                <span>Cargando...</span>
              </div>
            ) : items.length === 0 ? (
              <div className="iso9001-master-detail-empty">{emptyText}</div>
            ) : (
              items.map((item) => (
                <button
                  type="button"
                  key={String(item.id)}
                  title={item.title}
                  className={`iso9001-master-detail-card${selectedId === item.id ? " active" : ""}`}
                  onClick={() => onSelect(item.id)}
                >
                  <span className="iso9001-master-detail-label">{item.label}</span>
                  <span className="iso9001-master-detail-short-label">
                    {item.shortLabel ?? item.count}
                  </span>
                  <strong>{item.count}</strong>
                </button>
              ))
            )}
          </div>
        </aside>

        <div className="iso9001-master-detail-content">{children}</div>
      </div>
    </section>
  );
}
